'use client';

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Maximize2 } from 'lucide-react';
import type { FredRange, FredSeriesConfig } from '@/lib/fred-series';
import {
  TimeSeriesChart,
  prepareSeries,
  deriveRecessionRanges,
  type RecessionRange,
} from './TimeSeriesChart';
import { ChartExpandModal } from './ChartExpandModal';

// Re-exported for existing importers (EconomyTab, ShillerPeChart).
export { deriveRecessionRanges };
export type { RecessionRange };

interface FredSeriesPayload {
  seriesId: string;
  range: FredRange;
  observations: { date: string; value: number | null }[];
  lastUpdated: string;
}

interface FredChartProps {
  config: FredSeriesConfig;
  range: FredRange;
  recessionRanges: RecessionRange[];
  height?: number;
}

export function FredChart({ config, range, recessionRanges, height = 220 }: FredChartProps) {
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<FredSeriesPayload>({
    queryKey: ['/api/terminal/fred', config.id, range],
    queryFn: async () => {
      const res = await fetch(`/api/terminal/fred/${config.id}?range=${range}`);
      if (!res.ok) throw new Error('Failed to fetch FRED series');
      return res.json();
    },
    staleTime: 12 * 60 * 60 * 1000,
    retry: 1,
  });

  const prepared = useMemo(() => prepareSeries(data?.observations), [data?.observations]);
  const { hasData } = prepared;

  return (
    <div className="bg-[#111827] border border-[#1F2937] p-4">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">
          {config.title}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={`Expand ${config.title}`}
          title="Expand for detail"
          className="shrink-0 -mt-0.5 -mr-1 p-1 text-[#6B7280] hover:text-[var(--color-accent)] hover:bg-[#1F2937] rounded transition-colors"
        >
          <Maximize2 size={13} />
        </button>
      </div>
      <div className="text-[11px] text-[#6B7280] mb-3">{config.description}</div>

      {isLoading ? (
        <div className="w-full bg-[#1F2937] animate-pulse rounded" style={{ height }} />
      ) : isError || !hasData ? (
        <div
          className="w-full flex items-center justify-center text-xs text-[#6B7280] border border-dashed border-[#1F2937]"
          style={{ height }}
        >
          <div className="text-center">
            <div>{isError ? 'Data unavailable' : 'No data'}</div>
            {isError && (
              <button
                onClick={() => refetch()}
                className="mt-2 px-2 py-0.5 text-[10px] text-[var(--color-accent)] hover:underline"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      ) : (
        <TimeSeriesChart
          prepared={prepared}
          recessionRanges={recessionRanges}
          color={config.color}
          unit={config.unit}
          chartType={config.chartType}
          seriesLabel={config.title}
          height={height}
        />
      )}

      <ChartExpandModal
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        title={config.title}
        description={config.description}
        color={config.color}
        unit={config.unit}
        chartType={config.chartType}
        seriesLabel={config.title}
        initialRange={range}
        recessionRanges={recessionRanges}
        queryKey={(r) => ['/api/terminal/fred', config.id, r]}
        fetchUrl={(r) => `/api/terminal/fred/${config.id}?range=${r}`}
      />
    </div>
  );
}
